import { App, newApp, SystemEntity as ECSSystemEntity } from "@verit/ecs";
import { getSpeedMultiplier, onRender } from "./graphics/three.ts";
import { Entity as CommonEntity } from "@/shared/types.ts";
import { appContext, initApp } from "@/shared/context.ts";
import {
  buildDefaultMap,
  generateDoodads,
  setMapForApp,
} from "@/shared/map.ts";
import { editorVar } from "@/vars/editor.ts";

export type Entity = CommonEntity & {
  selected?: boolean;
  zIndex?: number;
  isKaboom?: boolean;
  /** Client-side elapsed time for easing calculations */
  clientElapsedTime?: number;
  aspectRatio?: number;
  /** Hidden by fog of war */
  hiddenByFog?: boolean;
};

export type SystemEntity<K extends keyof Entity> = ECSSystemEntity<Entity, K>;

type Listeners = {
  [key in keyof Entity]?: Set<((entity: Entity, prev: unknown) => void)>;
};
const allListeners = new WeakMap<Entity, Listeners>();

export const listen = <P extends keyof Entity>(
  entity: Entity,
  props: P | P[],
  fn: (entity: Entity, prev: Entity[P]) => void,
) => {
  const listeners = allListeners.get(entity);
  if (!listeners) return () => {};
  for (const prop of Array.isArray(props) ? props : [props]) {
    const set = listeners[prop] ?? (listeners[prop] = new Set());
    set.add(fn as (entity: Entity, prev: unknown) => void);
  }
  return () => {
    for (const prop of Array.isArray(props) ? props : [props]) {
      const set = listeners[prop];
      set?.delete(fn as (entity: Entity, prev: unknown) => void);
    }
  };
};

export const app = newApp<Entity>({
  initializeEntity: (entity: Partial<Entity>) => {
    if (!entity.id) throw new Error("Expected entity to have an id");
    // Newest entity on top; works with 2D graphics
    const listeners: Listeners = {};
    const proxy = new Proxy(entity as Entity, {
      set: (target, prop, value) => {
        const prev = target[prop as keyof Entity];
        if (prev === value) return true;
        // deno-lint-ignore no-explicit-any
        (target as any)[prop] = value;
        app.queueEntityChange(proxy, prop as keyof Entity);
        listeners[prop as keyof Entity]?.forEach((fn) => fn(proxy, prev));
        return true;
      },
      deleteProperty: (target, prop) => {
        const prev = target[prop as keyof Entity];
        if (prev == null) return true;
        // deno-lint-ignore no-explicit-any
        delete (target as any)[prop];
        app.queueEntityChange(proxy, prop as keyof Entity);
        listeners[prop as keyof Entity]?.forEach((fn) => fn(proxy, prev));
        return true;
      },
    });
    allListeners.set(proxy, listeners);
    return proxy;
  },
}) as App<Entity>;
// deno-lint-ignore no-explicit-any
(globalThis as any).app = app;

setMapForApp(app, buildDefaultMap());

appContext.current = app;

queueMicrotask(appContext.bind(initApp));

onRender((delta, time) => app.update(delta * getSpeedMultiplier(), time));

export const map: Record<string, Entity> = {};

let fogResetter: (() => void) | undefined;
export const registerFogReset = (fn: () => void) => {
  fogResetter = fn;
};

export const unloadEcs = (
  { includePlayers = false }: { includePlayers?: boolean } = {},
) =>
  app.batch(() => {
    const editor = editorVar();
    for (const entity of app.entities) {
      if (entity.type === "cosmetic" || entity.type === "static") {
        if (!editor) continue;
      }
      if (entity.isPlayer) continue;

      app.removeEntity(entity);
      delete map[entity.id];
    }
    for (const entity of app.entities) {
      if (
        !entity.isPlayer || (entity.id !== "practice-enemy" && !includePlayers)
      ) continue;

      app.removeEntity(entity);
      delete map[entity.id];
    }
    fogResetter?.();
  });

generateDoodads();
